import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import TyreDiagram from "../components/TyreDiagram";
import { StatusBadge } from "../components/StatusBadge";
import AdminEditDialog from "../components/AdminEditDialog";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, Truck, QrCode } from "@phosphor-icons/react";

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [v, setV] = useState(null);
  const [showQr, setShowQr] = useState(false);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  const load = () => api.get(`/vehicles/${id}`).then(({ data }) => { setV(data); setNotes(data.notes || ""); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.put(`/vehicles/${id}`, { notes });
      toast.success("Notes saved");
    } catch (e) { toast.error("Could not save notes"); }
    finally { setSavingNotes(false); }
  };
  if (!v) return <div className="text-zinc-500">Loading…</div>;

  const qrData = `${window.location.origin}/vehicles/${v.id}`;
  const specs = [
    ["Type", v.vehicle_type], ["Tyre Size", v.tyre_size], ["Capacity", v.capacity],
    ["Engine Hours", `${Math.round(v.current_hours || 0).toLocaleString()} h`],
    ["Vehicle KM", v.current_km != null ? `${Math.round(v.current_km).toLocaleString()} km` : "—"],
    ["Mine Site", v.mine_site_name], ["Positions", (v.positions_layout || []).length],
  ];

  return (
    <div className="space-y-6">
      <button data-testid="back-btn" onClick={() => navigate("/vehicles")} className="flex items-center gap-1 text-sm text-zinc-400 hover:text-amber-500">
        <ArrowLeft size={16} /> Back to Dumpers
      </button>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="overline text-amber-500 flex items-center gap-2"><Truck size={14} /> Dumper</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">{v.fleet_number}</h1>
          <div className="mt-1"><StatusBadge status={v.status} /></div>
        </div>
        <div className="flex gap-2">
          {hasRole?.("admin") && (
            <AdminEditDialog
              title={`Edit Dumper — ${v.fleet_number}`}
              endpoint={`/vehicles/${v.id}`}
              testid="edit-vehicle"
              onSaved={load}
              initial={{ current_hours: v.current_hours, current_km: v.current_km,
                make: v.make, model: v.model, vehicle_type: v.vehicle_type }}
              fields={[
                { name: "current_hours", label: "Engine Hours", type: "number" },
                { name: "current_km", label: "Vehicle KM", type: "number" },
                { name: "make", label: "Make" },
                { name: "model", label: "Model" },
                { name: "vehicle_type", label: "Vehicle Type" },
              ]}
            />
          )}
          <Button variant="outline" onClick={() => setShowQr(!showQr)} data-testid="vehicle-qr-btn">
            <QrCode size={16} className="mr-1" /> {showQr ? "Hide" : "Show"} QR Code
          </Button>
        </div>
      </div>

      {showQr && (
        <div className="flex justify-center rounded-sm border border-zinc-800 bg-white p-4">
          <img src={`${API}/qr?data=${encodeURIComponent(qrData)}`} alt="QR" className="h-48 w-48" />
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-sm border border-zinc-800 bg-zinc-900 p-5">
          <h2 className="mb-4 font-display text-lg font-bold tracking-tight">Specifications</h2>
          <div className="grid grid-cols-2 gap-3">
            {specs.map(([k, val]) => (
              <div key={k} className="rounded-sm border border-zinc-800 bg-zinc-950 p-3">
                <div className="overline text-zinc-500">{k}</div>
                <div className="mt-1 font-mono text-sm">{val || "—"}</div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h2 className="mb-4 font-display text-lg font-bold tracking-tight">Tyre Position Layout</h2>
          <TyreDiagram positions={v.positions_layout} fitted={v.fitted_positions} vehicleType={v.vehicle_type} onSelect={(t) => navigate(`/tyres/${t.tyre_id}`)} />
        </div>
      </div>

      <div className="rounded-sm border border-zinc-800 bg-zinc-900 p-5">
        <h2 className="mb-1 font-display text-lg font-bold tracking-tight">Notes</h2>
        <p className="mb-3 text-xs text-zinc-500">Quick notes for the crew — route, load type, reminders.</p>
        <Textarea data-testid="vehicle-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. Runs the north haul road; heavy load — check pressures daily." className="min-h-[90px] bg-zinc-950" />
        <div className="mt-3">
          <Button onClick={saveNotes} disabled={savingNotes} data-testid="save-notes-btn" className="bg-amber-500 font-bold text-zinc-950 hover:bg-amber-400">
            {savingNotes ? "Saving…" : "Save Notes"}
          </Button>
        </div>
      </div>
    </div>
  );
}
